import { useState, useEffect } from 'react';

import { APP_BUILD_ID } from '../lib/appVersion';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Written by scripts/runCra.js into the deploy root, so it sits beside index.html and is
// never part of the hashed bundle this tab already loaded.
const VERSION_URL = `${process.env.PUBLIC_URL}/version.json`;

// Returns the deployed build stamp once it differs from the one this tab is running, and
// null until then. Only `npm run build` writes version.json, so this stays null in dev.
export const useAppUpdate = () => {
  const [deployed, setDeployed] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
        // An SPA history fallback answers 200 with index.html when version.json is
        // absent, so an ok status alone does not mean there is a stamp to read.
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
          return;
        }
        const stamp = await response.json();
        if (cancelled || !stamp?.buildId || stamp.buildId === APP_BUILD_ID) return;
        setDeployed(stamp);
      } catch {
        // A failed poll changes nothing; the next interval or tab focus tries again.
      }
    };

    check();
    const timer = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return deployed;
};
